/**
 * İZLEME DURUMLARININ TÜRKÇESİ. İngilizce durum adı (`spot_ready` gibi) ekrana
 * BASILMAZ: kullanıcı bir durum makinesi okumak zorunda değil, ne beklemesi
 * gerektiğini okumak zorunda.
 *
 * `spot_ready` METNİNDE ÖZEL DİKKAT — Aşama 2'de değişti:
 * Anlık fiyat sunmayan kaynaklar (TEFAS fonları, TCMB kurları) anlık fiyat
 * OLMADAN `spot_ready`ye geçiyor (`packages/central/src/domain/state-machine.ts`
 * içindeki `spotExpected` notu). Bir fon için "güncel fiyat" son açıklanan
 * birim pay değeridir ve DAHA TAZESİ MEVCUT DEĞİL. Bu yüzden metin "anlık
 * fiyat bekleniyor" ya da "canlı fiyat" İMA ETMEZ; varlık sınıfı biliniyorsa
 * dil ona göre daralır.
 */

import { SPOT_READY_TARGET_SECONDS } from "@parafon/contracts/central-api";
import type { HorizonLimit } from "@parafon/contracts/central-api";
import type { IsoDate, TrackState } from "@parafon/contracts";
import { M } from "../dil";

export type StateTone = "hazir" | "hazirlaniyor" | "durdu" | "elle";

export interface TrackStateView {
  /** Kullanıcı dilinde durum adı. */
  readonly title: string;
  /** Ne anlama geldiği ve kullanıcının ne yapması gerektiği. */
  readonly meaning: string;
  readonly tone: StateTone;
  /** Elle değerlemeye yönlendirme gerekiyor mu. */
  readonly needsManual: boolean;
}

/**
 * Anlık fiyat yayınlamayan varlık sınıfları. Liste MERKEZİN OLGUSUNUN
 * KOPYASI DEĞİL: merkez kararı kaynak başına (`providesSpot`) veriyor ve
 * cihaza yalnızca sonucu (`spot_ready`) geliyor. Burada yapılan tek şey
 * METNİ yumuşatmak — durum kararı değiştirilmiyor.
 * AŞAMA KAPISI: `TrackStatusDto` bir `spotExpected` alanı taşısaydı bu tahmin
 * hiç gerekmezdi (raporda bildirildi).
 */
const NO_INTRADAY_CLASSES: readonly string[] = ["fund_tr", "fx", "deposit"];

/**
 * UFKUN NİÇİN ORADA OLDUĞUNU SÖYLEYEN CÜMLE.
 *
 * Bu fonksiyon bir dönem YAZILAMIYORDU: `TrackStatusDto` sebebi taşımıyordu ve
 * metin "on yıllık geçmiş" diyordu — TEFAS fonları için düpedüz yanlış, çünkü
 * kaynağın arşivi 5 yıl. Sözleşme artık `horizonLimitedBy` taşıyor
 * (merkez şema v8, cihaz v4), dolayısıyla tahmin de gerekmiyor.
 *
 * AYRIM RAPOR İÇİN KRİTİK ve cümleler o yüzden farklı: ihraç tarihinden dolayı
 * kısa olan ufuk EKSİKLİK DEĞİL (araç yoktu, hesaplanacak bir şey yok);
 * kaynak tabanından dolayı kısa olan ise GERÇEK eksiklik (fiyat piyasada
 * vardı, bizde yok). Kullanıcının bu ikisini ayırt edebilmesi gerekiyor,
 * yoksa ilkini arıza sanıp uğraşır, ikincisini normal sanıp güvenir.
 *
 * `null` = merkez söylemedi (eski sürüm ya da künye henüz gelmedi): tahmin
 * YOK, yalnızca kesin olan söyleniyor.
 */
export function describeHorizonLimit(limitedBy: HorizonLimit | null): string {
  switch (limitedBy) {
    case "target":
      return M.izleme.durum.ufukHedef;
    case "inception":
      return M.izleme.durum.ufukIhrac;
    case "source":
      return M.izleme.durum.ufukKaynak;
    default:
      return M.izleme.durum.ufukSebepsiz;
  }
}

export function describeTrackState(
  state: TrackState,
  options: {
    readonly assetClass?: string | null;
    readonly horizonLimitedBy?: HorizonLimit | null;
  } = {},
): TrackStateView {
  const assetClass = options.assetClass ?? null;
  const noIntraday = assetClass !== null && NO_INTRADAY_CLASSES.includes(assetClass);
  const limitedBy = options.horizonLimitedBy ?? null;
  /**
   * "ON YIL" İDDİASI ARTIK KOŞULLU. Kaynak tabanıyla kırpılmış bir araçta
   * "on yıllık geçmiş doluyor" demek, hiç gelmeyecek bir şeyi bekletmek olur.
   */
  const gecmisIfadesi = limitedBy === "source" ? M.izleme.durum.gecmisKaynak : M.izleme.durum.gecmis;

  switch (state) {
    case "pending":
      return {
        title: M.izleme.durum.pendingBaslik,
        meaning: M.izleme.durum.pendingAnlam(SPOT_READY_TARGET_SECONDS),
        tone: "hazirlaniyor",
        needsManual: false,
      };
    case "spot_ready":
      return {
        title: noIntraday ? M.izleme.durum.spotGunIciYokBaslik : M.izleme.durum.spotBaslik,
        meaning: noIntraday
          ? // "GÜN SONUNDA" DEĞİL: kullanıcının düzeltmesi — TEFAS bir günün
            // fiyatını o gün en geç 11:00'de yayınlıyor, gün sonunda değil.
            // Metin yine de saat SÖYLEMİYOR, çünkü bu cümle üç varlık sınıfı
            // için birden kullanılıyor (fon, kur, mevduat) ve saatleri farklı;
            // ortak ve doğru olan olgu "günde bir kez yayınlanır".
            M.izleme.durum.spotGunIciYokAnlam(gecmisIfadesi)
          : M.izleme.durum.spotAnlam(gecmisIfadesi),
        tone: "hazirlaniyor",
        needsManual: false,
      };
    case "backfilling":
      return {
        title: M.izleme.durum.backfillingBaslik,
        meaning: M.izleme.durum.backfillingAnlam(gecmisIfadesi, describeHorizonLimit(limitedBy)),
        tone: "hazirlaniyor",
        needsManual: false,
      };
    case "ready":
      return {
        title: M.izleme.durum.readyBaslik,
        meaning:
          // "ON YIL TAM" İDDİASI KALDIRILDI ve sebebi canlı bir bulgu: TEFAS 5
          // yıldan eski veri vermiyor, yani fonların ufku zorunlu olarak
          // kırpılıyor. Eski cümle o araçlar için düpedüz yanlıştı.
          //
          // SEBEBİ ARTIK SÖYLÜYOR: `horizonLimitedBy` sözleşmeye eklendi
          // (madde 13), yani "bilmediğimiz bir ayrımı tahmin etmemek" için
          // yazılan eski çekince ARTIK GEÇERSİZ. Bilgi geldi, cümle de onu
          // kullanıyor.
          M.izleme.durum.readyAnlam(describeHorizonLimit(limitedBy)),
        tone: "hazir",
        needsManual: false,
      };
    case "dormant":
      return {
        title: M.izleme.durum.dormantBaslik,
        meaning: M.izleme.durum.dormantAnlam,
        tone: "durdu",
        needsManual: false,
      };
    case "unresolved":
      return {
        title: M.izleme.durum.unresolvedBaslik,
        meaning: M.izleme.durum.unresolvedAnlam,
        tone: "elle",
        needsManual: true,
      };
  }
}

// ---------------------------------------------------------------------------
// Geçmiş ilerlemesi
// ---------------------------------------------------------------------------

/**
 * `horizonFrom`..bugün aralığının ne kadarı dolu. `backfilledTo` "geçmiş
 * buraya kadar tam" demektir, yani dolu olan aralık [backfilledTo, bugün].
 *
 * `bilinmiyor` DURUMU AYRI: `backfilledTo === null` iken "%0" göstermek,
 * "hiç veri yok" ile "sıfır kadar veri var"ı aynı sayıya indirmek olurdu.
 * Arayüz o durumda sayı değil cümle gösterir.
 */
export type BackfillProgress =
  | { readonly kind: "bilinmiyor" }
  | { readonly kind: "baslamadi" }
  | { readonly kind: "kismi"; readonly ratio: number; readonly coveredFrom: IsoDate }
  | { readonly kind: "tam"; readonly coveredFrom: IsoDate };

export function backfillProgress(
  horizonFrom: IsoDate | null,
  backfilledTo: IsoDate | null,
  today: IsoDate,
): BackfillProgress {
  if (horizonFrom === null) return { kind: "bilinmiyor" };
  if (backfilledTo === null) return { kind: "baslamadi" };
  if (backfilledTo <= horizonFrom) return { kind: "tam", coveredFrom: horizonFrom };
  const total = dayDiff(horizonFrom, today);
  const covered = dayDiff(backfilledTo, today);
  if (total <= 0) return { kind: "tam", coveredFrom: horizonFrom };
  const ratio = Math.max(0, Math.min(1, covered / total));
  return { kind: "kismi", ratio, coveredFrom: backfilledTo };
}

function dayDiff(from: IsoDate, to: IsoDate): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** Eksik dönemin sebebi — kullanıcı diliyle. */
export const MISSING_REASON_LABEL: Readonly<Record<string, string>> = M.izleme.eksikSebep;
